/* istanbul ignore file */
import React, { useState, useEffect } from 'react';
import './audio.css';
import { RecordVoiceOver, Stop, DeleteForever, MicNone } from '@material-ui/icons';
import AudioRecorder from './Record';
import useTranslation from '../../../hooks/use-translation';
import { postCoughCollectedSample } from '../../../utils/network';
import { trimLowerCase } from '../../../utils/utils'

const DEFAULT_RECORDING_TIME = 2;
const DEFAULT_MAX_RECORDINGS = 3;
const inlineStyles = {
  chatBubble: {
    width: '80%',
    borderRadius: '1em',
    background: '#eeeeee',
    borderTopLeftRadius: '3px',
    color: '#7f7f7f',
    padding: '1em',
    display: 'flex',
    flexDirection: 'column'
  },
  actionButtonContainer: {
    questionnaire: {
      paddingTop: '1em',
      paddingBottom: '1em',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    chatbot: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  },
  actionButton: {
    questionnaire: {
      height: '5em',
      width: '5em',
      color: 'white',
      borderRadius: 50,
      borderColor: '#039073',
      borderWidth: '0px',
      boxShadow: '0em 0em 1em #888888'
    },
    chatbot: {
      height: '3em',
      width: '3em',
      color: 'white',
      borderRadius: 50,
      borderColor: '#039073',
      borderWidth: '0px',
      boxShadow: '0em 0em 1em #888888'
    }
  },
  actionIcon: {
    questionnaire: {
      fontSize: '3.5em'
    },
    chatbot: {
      fontSize: '2em'
    },
  },
  startText: {
    questionnaire: {
      color: '#039073', fontWeight: 'bold', fontSize: '16px', marginTop: '4px'
    },
    chatbot: {
      color: '#039073', fontWeight: 'bold', fontSize: '12px', marginTop: '4px'
    }
  },
  recordingStatus: {
    questionnaire: {
      paddingTop: '0.5em', fontSize: '2em', display: 'flex', justifyContent: 'center', fontWeight: '500', color: '#039073'
    },
    chatbot: {
      paddingTop: '0.5em', fontSize: '1.1em', display: 'flex', justifyContent: 'center', fontWeight: '500', color: '#039073'
    }
  }
}
const getDisplayType = (hasChatBotQuestionnaire) => {
  if (hasChatBotQuestionnaire())
    return 'chatbot';
  return 'questionnaire';
}

const getTimerInterval = (recordingDuration) => {
  return 1000 + (120) / (recordingDuration);
}


function renderingRecordingStatus(recordings, maxRecordings, t, hasChatBotQuestionnaire) {
  const style = inlineStyles.recordingStatus[getDisplayType(hasChatBotQuestionnaire)];
  return (<div style={style}>
    {recordings.length}/{maxRecordings} {t('COUGH_RECORDING')}</div>);
}
function renderRecordingButtonAndText(recordings, maxRecordings, t, toggle, hasChatBotQuestionnaire) {
  return <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
    <button id="start" style={{ ...inlineStyles.actionButton[getDisplayType(hasChatBotQuestionnaire)], ...{ background: 'white' } }} onClick={() => {
      if (recordings && recordings.length >= maxRecordings) {
        alert(`${t('ADD_MORE_THAN')} ${maxRecordings} ${t('RECORDINGS')}`);
        return;
      }
      toggle('recording');
    }}>
      <MicNone style={{ ...inlineStyles.actionIcon[getDisplayType(hasChatBotQuestionnaire)], ...{ color: '#039073' } }} />
    </button>

    <p style={{ color: '#039073', fontWeight: 'bold', fontSize: '12px', marginTop: '4px' }}>
      {t('TAP_TO_START')}
    </p>
  </div>;
}

function renderStopButton(toggle, reset, t, hasChatBotQuestionnaire) {
  return <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
    <button id="stop" style={{ ...inlineStyles.actionButton[getDisplayType(hasChatBotQuestionnaire)], ...{ background: '#ff073a' } }} onClick={() => {
      toggle('inactive');
      reset();
    }}>
      <Stop style={{ ...inlineStyles.actionIcon[getDisplayType(hasChatBotQuestionnaire)], ...{ color: 'black' } }} />
    </button>
    <p style={{ color: '#ff073a', fontWeight: 'bold' }}>
      {t('UPLOAD_TITLE')}
    </p>
  </div>;
}

function renderUploadButton(recordings, submitAudio, t) {
  return <div style={{ display: 'flex', justifyContent: 'center' }}>
    {recordings.length > 0
      ? <button className="submitButton" onClick={submitAudio} type="button">
        {t('UPLOAD_TITLE')}
      </button>
      : null}
  </div>;
}

function renderDeleteButton(triggerDelete, f, index) {
  return <button style={{ height: '2.5em', width: '2.5em', color: 'white', borderRadius: 50, background: "#039073", borderColor: '#039073', borderWidth: '0px', boxShadow: '0em 0em 0.5em #888888' }} onClick={() => {
    triggerDelete(f, index);
  }}>
    <DeleteForever style={{ fontSize: '20px' }} />
  </button>;
}

function renderAudioControl(f) {
  return <audio controls autoPlay style={{ width: '250px' }} name="media">
    <source src={f} className="bg-red" type="audio/wav" />
  </audio>;
}

function renderButtons(recordStatus, recordings, maxRecordings, t, toggle, reset, hasChatBotQuestionnaire) {
  const style = inlineStyles.actionButtonContainer[getDisplayType(hasChatBotQuestionnaire)]
  return <div style={style}>
    {recordStatus !== 'recording'
      && (renderRecordingButtonAndText(recordings, maxRecordings, t, toggle, hasChatBotQuestionnaire))}
    {(recordStatus === 'paused' || recordStatus === 'recording')
      &&
      renderStopButton(toggle, reset, t, hasChatBotQuestionnaire)}
  </div>;
}


export default props => {
  const { t, hasChatBotQuestionnaire } = useTranslation();
  const maxRecordings = props && props.question && props.question.response_length ? props.question.response_length : DEFAULT_MAX_RECORDINGS;
  const [timerValue, setTimerValue] = useState(DEFAULT_RECORDING_TIME);
  const [recordStatus, setRecordStatus] = useState('');
  const [recordings, setRecordings] = useState([]);

  const toggle = status => {
    setRecordStatus(status);
  };

  const reset = () => {
    setTimerValue(DEFAULT_RECORDING_TIME);
  };

  useEffect(() => {
    let interval = null;
    if (timerValue === 0) {
      timerSetupForRecording(setTimerValue, toggle, reset);
    }
    let __return;
    ({ __return, interval } = performTimer(recordStatus, interval, setTimerValue, timerValue));
    return __return;
  }, [recordStatus, timerValue]);

  const addRecordings = addRecordingFunction(recordings, setRecordings);
  const deleteRecording = deleteRecordingFunction(recordings, setRecordings)
  const triggerDelete = triggerDeleteFunction(t, recordings, setRecordings);
  const submitAudio = submitAudioFunction(recordings, deleteRecording, props);

  if (hasChatBotQuestionnaire())
    return renderRecordUiComponentsForChatBot(recordings, maxRecordings, t, timerValue, recordStatus, addRecordings, toggle, reset, triggerDelete, submitAudio, hasChatBotQuestionnaire)
  return (
    renderRecorderUIComponents(recordings, maxRecordings, t, timerValue, recordStatus, addRecordings, toggle, reset, triggerDelete, submitAudio, hasChatBotQuestionnaire)
  );
};


function timerSetupForRecording(setTimerValue, toggle, reset) {
  setTimerValue(0);
  toggle('inactive');
  reset();
}

function renderRecordUiComponentsForChatBot(recordings, maxRecordings, t, timerValue, recordStatus, addRecordings, toggle, reset, triggerDelete, submitAudio, hasChatBotQuestionnaire) {
  return <div style={inlineStyles.chatBubble}>
    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
      <div style={{ fontSize: '2em', color: '#ff073a' }}>
        {timerValue}{' '}{t('SECOND')}
      </div>
      {renderButtons(recordStatus, recordings, maxRecordings, t, toggle, reset, hasChatBotQuestionnaire)}
    </div>
    <div style={{ marginTop: '-2em' }}></div>
    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <AudioRecorder status={recordStatus} onAddRecordings={addRecordings} />
      </div>
      <div style={{ width: '2em' }} />
      {renderingRecordingStatus(recordings, maxRecordings, t, hasChatBotQuestionnaire)}
    </div>
    <div style={{ marginTop: '-2em' }} />
    {renderRecordedFilesInList(recordings, triggerDelete)}
    {renderUploadButton(recordings, submitAudio, t)}
  </div>;
}

function renderRecorderUIComponents(recordings, maxRecordings, t, timerValue, recordStatus, addRecordings, toggle, reset, triggerDelete, submitAudio, hasChatBotQuestionnaire) {
  return <div>
    {renderingRecordingStatus(recordings, maxRecordings, t, hasChatBotQuestionnaire)}
    <div style={{ paddingTop: '0.5em', paddingBottom: '0.5em', fontSize: '3em', display: 'flex', justifyContent: 'center', color: '#ff073a' }}>
      {timerValue}{' '}{t('SECOND')}
    </div>

    <div style={{ fontSize: '1em', display: 'flex', justifyContent: 'center', color: '#039073' }}>
      <RecordVoiceOver style={{ fontSize: '80px' }} />
    </div>

    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <AudioRecorder status={recordStatus} onAddRecordings={addRecordings} width="400%" />
    </div>
    {renderButtons(recordStatus, recordings, maxRecordings, t, toggle, reset, hasChatBotQuestionnaire)}
    {renderRecordedFilesInList(recordings, triggerDelete)}
    {renderUploadButton(recordings, submitAudio, t)}
  </div>;
}

function triggerDeleteFunction(t, recordings, setRecordings) {
  return (f, index) => {
    if (window.confirm(t('DELETE_CONFIRMATION'))) {
      let it;
      const newRecordings = [];
      for (it = 0; it < recordings.length; it++) {
        if (recordings[it] !== f) {
          newRecordings.push(recordings[it]);
        }
      }
      setRecordings([...newRecordings]);
    }
  };
}

function deleteRecordingFunction(recordings, setRecordings) {
  return index => {
    if (recordings.length - 1 >= index) {
      const recordingsList = recordings;
      recordingsList.splice(index, 1);
      setRecordings(recordingsList);
    }
    else {
      setRecordings([]);
    }
  };
}

function addRecordingFunction(recordings, setRecordings) {
  return f => {
    const recordingsList = [];
    recordings.forEach(fi => {
      recordingsList.push(fi);
    });
    recordingsList.push(f);
    setRecordings(recordingsList);
  };
}

function submitAudioFunction(recordings, deleteRecording, props) {
  return async () => {
    const requests = recordings.map(file => {
      const base64File = file[1];
      const audioFormat = file[0].split('/')[1].split(';')[0];
      return postCoughCollectedSample(base64File, audioFormat);
    });
    const promiseResponses = await Promise.all(requests);
    let uris = [];
    promiseResponses.map((promiseResponse, index) => {
      if (!promiseResponse.status || trimLowerCase(promiseResponse.status) === 'invalid') {
        deleteRecording(index);
      }
      else {
        const { uri = 'error' } = promiseResponse;
        uris.push(uri);
      }
    });
    if (uris.length >= 1) {
      props.onSubmit(uris);
    }
    else {
      alert('Recorded Audios are invalid');
    }
  };
}

function performTimer(recordStatus, interval, setTimerValue, timerValue) {
  if (recordStatus === 'recording') {
    interval = setInterval(() => {
      setTimerValue(timerValue => timerValue - 1);
    }, getTimerInterval(DEFAULT_RECORDING_TIME));
  }
  else if (recordStatus === 'paused' && timerValue !== DEFAULT_RECORDING_TIME) {
    clearInterval(interval);
  }
  return { __return: () => clearInterval(interval), interval };
}

function renderRecordedFilesInList(recordings, triggerDelete) {
  return <div style={{
    paddingBottom: '1em', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'column'
  }}>
    {recordings.map((f, index) => (<div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row', marginTop: '1em'
    }} className={`file${index}`} key={f.toString()}>
      {renderAudioControl(f)}
      {renderDeleteButton(triggerDelete, f, index)}
    </div>))}
  </div>;
}


